import { useEffect, useState } from 'react';
import { LayoutGrid, Flashlight, Thermometer, Sun, Folder, Zap, ArrowUpRight } from 'lucide-react';

export default function Projects() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [projects, setProjects] = useState<any[]>([]);

  useEffect(() => {
    const url = `${import.meta.env.BASE_URL}ciacf/projects.json`;
    fetch(url)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        if (Array.isArray(data)) setProjects(data);
      })
      .catch(() => {
        setProjects([]);
      });
  }, []);

  const categories = [
    { id: 'all', name: 'Todos', icon: <LayoutGrid size={16} /> },
    ...(Array.from(new Set(projects.map(p => p.category).filter(Boolean))).map((c) => {
      const map: Record<string, { name: string; icon: any }> = {
        electrical: { name: 'Sistemas Eléctricos', icon: <Flashlight size={16} /> },
        heatpump: { name: 'Bombas de Calor', icon: <Thermometer size={16} /> },
        solar: { name: 'Energía Solar', icon: <Sun size={16} /> }
      };
      const m = map[c as string] || { name: (c as string).charAt(0).toUpperCase() + (c as string).slice(1), icon: <Folder size={16} /> };
      return { id: c as string, name: m.name, icon: m.icon };
    }))
  ];

  const filteredProjects = activeCategory === 'all'
    ? projects
    : projects.filter(p => p.category === activeCategory);

  return (
    <section id="proyectos" className="py-32 bg-slate-50 relative overflow-hidden">
      <div className="section-container relative z-10">
        <div className="flex flex-col md:flex-row items-end justify-between mb-20 gap-8">
          <div className="max-w-2xl">
            <div className="badge-premium mb-6">
              <Zap size={14} className="text-accent" />
              <span>Portafolio Industrial</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-black text-primary-dark tracking-tighter leading-tight">
              Ingeniería en <span className="text-slate-400">Acción</span>
            </h2>
          </div>

          <div className="flex flex-wrap gap-3">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-bold text-xs uppercase tracking-widest transition-all duration-300
                  ${activeCategory === cat.id
                    ? 'bg-primary text-white shadow-premium scale-105'
                    : 'bg-white text-slate-400 hover:bg-slate-100 hover:text-primary-dark border border-slate-100'
                  }`}
              >
                {cat.icon}
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.length === 0 && (
            <div className="col-span-full text-center py-32 glass-card rounded-[3rem]">
              <div className="animate-pulse flex flex-col items-center">
                <div className="w-16 h-16 bg-slate-100 rounded-full mb-4" />
                <div className="h-4 w-48 bg-slate-100 rounded mb-2" />
                <div className="h-3 w-32 bg-slate-100 rounded" />
              </div>
            </div>
          )}

          {filteredProjects.map((project, index) => (
            <div key={index} className="group flex flex-col bg-white rounded-[2.5rem] overflow-hidden border border-slate-100 hover:shadow-premium-hover transition-all duration-500">
              <div className="relative h-72 overflow-hidden">
                <img
                  src={project.image || `${import.meta.env.BASE_URL}images/projects/factory.jpg`}
                  alt={project.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                  onError={(e) => {
                    const target = e.currentTarget as HTMLImageElement;
                    if (project.title.toLowerCase().includes('solar') || project.title.toLowerCase().includes('fotov')) {
                      target.src = `${import.meta.env.BASE_URL}images/projects/residential.jpg`;
                    } else if (project.title.toLowerCase().includes('luminaria') || project.title.toLowerCase().includes('ilumina')) {
                      target.src = `${import.meta.env.BASE_URL}images/projects/mall.jpg`;
                    } else {
                      target.src = `${import.meta.env.BASE_URL}images/projects/factory.jpg`;
                    }
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-900/80 via-transparent to-transparent opacity-60" />

                {/* Visual Pill for Client */}
                {project.client && (
                  <div className="absolute top-6 left-6">
                    <div className="px-4 py-1.5 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-[10px] font-bold text-white uppercase tracking-widest">
                      {project.client}
                    </div>
                  </div>
                )}

                {project.stats && (
                  <div className="absolute bottom-6 left-6 right-6 flex gap-3 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                    <div className="flex-1 glass-card p-3 bg-white/10 border-white/20 rounded-2xl">
                      <div className="text-[8px] uppercase font-black text-white/50 tracking-widest mb-1">Capacidad</div>
                      <div className="text-xs font-bold text-white tracking-widest">{project.stats.power}</div>
                    </div>
                    <div className="flex-1 glass-card p-3 bg-accent/20 border-accent/30 rounded-2xl">
                      <div className="text-[8px] uppercase font-black text-white/50 tracking-widest mb-1">Impacto</div>
                      <div className="text-xs font-bold text-white tracking-widest">{project.stats.savings}</div>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-10 flex-grow flex flex-col">
                <h3 className="text-2xl font-black text-primary-dark mb-4 tracking-tight leading-tight group-hover:text-accent transition-colors">
                  {project.title}
                </h3>
                <p className="text-slate-500 font-light text-sm leading-relaxed mb-8 flex-grow">
                  {project.description}
                </p>
                <div className="pt-8 border-t border-slate-50 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-accent" />
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Ejecución Finalizada</span>
                  </div>
                  <button className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-primary-dark group-hover:bg-primary-dark group-hover:text-white transition-all transform group-hover:rotate-45">
                    <ArrowUpRight size={20} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


